import { useState, useCallback, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { tours, tourCategories, TOUR_ROUTES } from '../lib/tourDefinitions';
import {
  LayoutDashboard, FileText, Receipt, BookOpen, ListTree, Landmark,
  Users, BarChart2, ClipboardList, Timer, Brain, Settings, GraduationCap,
  Repeat, Inbox, Play, CheckCircle2, Map, Trophy, Medal, Sparkles, ScanLine,
  Globe, MessageSquare
} from 'lucide-react';
import { cn } from '../lib/utils';

const ICON_MAP = {
  'layout-dashboard': LayoutDashboard, 'file-text': FileText, 'receipt': Receipt,
  'book-open': BookOpen, 'list-tree': ListTree, 'landmark': Landmark,
  'users': Users, 'bar-chart-2': BarChart2, 'clipboard-list': ClipboardList,
  'timer': Timer, 'brain': Brain, 'settings': Settings, 'graduation-cap': GraduationCap,
  'repeat': Repeat, 'inbox': Inbox, 'trophy': Trophy, 'medal': Medal,
  'sparkles': Sparkles, 'scan-line': ScanLine, 'map': Map,
  'globe': Globe, 'message-square': MessageSquare,
};

export default function GuidedTours() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Auto-start tour from query param
  useEffect(() => {
    const startId = searchParams.get('start');
    if (startId && tours[startId]) {
      startTour(startId);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Read completed tours from localStorage
  const getCompleted = useCallback(() => {
    try { return JSON.parse(localStorage.getItem('completedTours') || '[]'); } catch { return []; }
  }, []);

  const markComplete = useCallback((tourId) => {
    const completed = getCompleted();
    if (!completed.includes(tourId)) {
      localStorage.setItem('completedTours', JSON.stringify([...completed, tourId]));
    }
  }, [getCompleted]);

  const startTour = (tourId) => {
    const route = TOUR_ROUTES[tourId];
    if (route) {
      localStorage.setItem('pendingTour', tourId);
      navigate(route);
    }
  };

  const completed = getCompleted();
  const totalTours = Object.keys(tours).length;
  const completedCount = completed.filter(id => tours[id]).length;

  return (
    <div className="space-y-6" data-testid="guided-tours-page">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center shadow-lg shadow-cyan-500/20">
          <Map className="w-5 h-5 text-white" />
        </div>
        <div>
          <h1 className="text-2xl font-bold tracking-tight" data-testid="guided-tours-heading">Guided Tours</h1>
          <p className="text-sm text-muted-foreground">Interactive walkthroughs for every feature in CountBucks</p>
        </div>
      </div>

      {/* Progress */}
      <Card className="bg-gradient-to-r from-cyan-50 via-background to-blue-50 dark:from-cyan-950/20 dark:to-blue-950/20">
        <CardContent className="pt-5 pb-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium">{completedCount} of {totalTours} tours completed</span>
            <span className="text-sm font-bold" style={{ color: completedCount === totalTours ? '#10b981' : '#0891b2' }}>
              {Math.round((completedCount / totalTours) * 100)}%
            </span>
          </div>
          <div className="w-full bg-muted rounded-full h-2">
            <div className="bg-gradient-to-r from-cyan-500 to-blue-500 h-2 rounded-full transition-all" style={{ width: `${(completedCount / totalTours) * 100}%` }} />
          </div>
        </CardContent>
      </Card>

      {/* Tour Categories */}
      {tourCategories.map((cat) => (
        <div key={cat.name} className="space-y-3">
          <h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">{cat.name}</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {cat.tours.map((tourId) => {
              const tour = tours[tourId];
              if (!tour) return null;
              const Icon = ICON_MAP[tour.icon] || LayoutDashboard;
              const isDone = completed.includes(tourId);
              return (
                <Card
                  key={tourId}
                  className={cn("group hover:border-primary/30 transition-all cursor-pointer", isDone && "border-emerald-500/20 bg-emerald-50/30 dark:bg-emerald-950/10")}
                  onClick={() => startTour(tourId)}
                  data-testid={`tour-card-${tourId}`}
                >
                  <CardContent className="pt-4 pb-4">
                    <div className="flex items-start gap-3">
                      <div className={cn("w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 transition-transform group-hover:scale-110", isDone ? "bg-emerald-100 dark:bg-emerald-900/30" : "bg-muted")}>
                        {isDone ? <CheckCircle2 className="w-4.5 h-4.5 text-emerald-500" /> : <Icon className="w-4.5 h-4.5 text-muted-foreground" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <h3 className="font-semibold text-sm">{tour.title}</h3>
                        </div>
                        <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{tour.description}</p>
                        <div className="flex items-center gap-2 mt-2">
                          <Badge variant="secondary" className="text-[10px] px-1.5 py-0">
                            {tour.steps.length} steps
                          </Badge>
                          {isDone && <Badge className="text-[10px] px-1.5 py-0 bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400">Completed</Badge>}
                        </div>
                      </div>
                      <Button variant="ghost" size="icon" className="h-8 w-8 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">
                        <Play className="w-4 h-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}
